import { useCallback, useState } from 'react';

import { useManualQuery } from '@zenbusiness/application-commons-graphql';
import useLocalStorage from '@zenbusiness/application-commons-hooks/useLocalStorage';
import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';

import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import DepositMoneyModal from '#client/components/MoveMoney/DepositMoneyModal';
import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { MODAL_NAMES } from '#client/constants/modals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';
import { GET_LIST_COUNTERPARTY } from '#client/queries';

export default function useAddBankOrDeposit({ actionsKit, counterParty } = {}) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const [loading, setLoading] = useState(false);

  const { openModal } = useModalContext();
  const { createCommonErrorToast } = useToastContext();

  const [getListCounterPartyPlaid, { errors, error: listCounterpartyError }] = useManualQuery(
    GET_LIST_COUNTERPARTY,
    {
      noThrow: true
    }
  );

  useLogError(errors, listCounterpartyError);
  /**
   * When counterparty is empty then open the link a bank account modal
   * When counterparty is not empty then open the deposit money modal (show list counterparty plaid)
   */
  const [hasAccountCache, setHasAccountCache] = useLocalStorage(
    `has_counterparty.${businessEntityUuid}`,
    false
  );
  const openAddBankOrDeposit = useCallback(async () => {
    const hasAccount = hasAccountCache === true;
    if (!hasAccount) {
      const { data, error } = await getListCounterPartyPlaid({
        variables: {
          page: {
            pageSize: 1,
            page: 1
          },
          filter: {
            connectedType: COUNTERPARTY_CONNECTED_TYPE.PLAID,
            businessEntityUuid
          }
        },
        skipCache: true
      });

      if (error || isUndefinedOrNull(data.bankingCounterparties)) {
        createCommonErrorToast({ error });
        return;
      }

      const accounts = data?.bankingCounterparties?.bankingCounterparties;
      const numberOfAccount = accounts?.length;

      if (numberOfAccount === 0) {
        openModal({
          name: MODAL_NAMES.LINK_BANK_ACCOUNT_MODAL,
          component: AddCounterPartyModal,
          mode: AddCounterPartyModal.MODAL_TYPES.ADD_BANK,
          actionsKit
        });
        return;
      }

      if (numberOfAccount > 0) {
        setHasAccountCache(true);
      }
    }

    openModal({
      component: DepositMoneyModal,
      name: MODAL_NAMES.DEPOSIT_MONEY_MODAL,
      actionsKit,
      counterParty
    });
  }, [
    hasAccountCache,
    businessEntityUuid,
    openModal,
    actionsKit,
    counterParty,
    getListCounterPartyPlaid,
    createCommonErrorToast,
    setHasAccountCache
  ]);

  const openAddBankOrDepositWithLoading = useCallback(async () => {
    setLoading(true);
    await openAddBankOrDeposit();
    setLoading(false);
  }, [openAddBankOrDeposit]);

  return { openAddBankOrDeposit: openAddBankOrDepositWithLoading, loading };
}
